/**
 * Routing Engine — decides WHERE work belongs, regardless of how it arrived.
 *
 * The Attention Engine decides what deserves attention; this decides where a piece of
 * work goes. It is source-agnostic on purpose: email, the solicitor portal, a clerk
 * message, a conference follow-up, a hearing outcome, a document upload, a mobile
 * capture, the API, or an AI extraction all normalise to the SAME WorkItem and route
 * the SAME way. The engine never mentions "email" — it reasons about work. Add a new
 * producer tomorrow → it plugs in without touching the routing logic.
 *
 * (Naming: this is the "Work Queue", not an inbox — the professional cares that
 * something needs them, not that it came from Gmail.)
 */

export type WorkSource =
  | 'email' | 'portal' | 'clerk' | 'conference' | 'hearing'
  | 'document' | 'mobile' | 'api' | 'ai' | 'task' | 'system';

/** Where a piece of work belongs / what it needs next — the one primary disposition. */
export type WorkDisposition =
  | 'needs_new_matter'        // new instruction not yet in the system
  | 'needs_decision'          // an approval/decision blocking others (fee/conference/brief/conflict)
  | 'needs_reply'             // someone is waiting on an answer
  | 'needs_diary'             // carries a date that must not be missed
  | 'needs_document_review'   // a document to read/file
  | 'needs_billing'           // work/remittance to invoice or reconcile
  | 'needs_task'              // an action to do (record outcome, chase, follow-up)
  | 'needs_existing_matter'   // belongs on a known matter — file / read there
  | 'needs_nothing';          // informational — no action

/** Normalised unit of work from ANY producer. Producers map their payload to this. */
export interface WorkItem {
  id: string;
  source: WorkSource;
  /** Producer hint (e.g. 'message_received', 'hearing_outcome_pending', 'document_uploaded'). */
  kind: string;
  title: string;
  /** Linked to a known matter? null = not yet placed. */
  matterId?: string | null;
  /** A brand-new instruction/brief with no matter yet. */
  isInstruction?: boolean;
  /** Inbound message awaiting a response from the barrister. */
  needsReply?: boolean;
  /** Carries a date to diarise (hearing listed, filing deadline). */
  hasDeadline?: boolean;
  /** Money to raise or reconcile (fee to bill, remittance received). */
  isBillable?: boolean;
  /** A document that needs reviewing/filing. */
  isDocument?: boolean;
  /** An approval/decision blocking someone (fee/conference/brief approval, conflict override). */
  isDecision?: boolean;
  /** A concrete action to perform (record outcome, chase, prepare). */
  isActionable?: boolean;
}

export interface RoutingDecision {
  disposition: WorkDisposition;
  matterId: string | null;
  reason: string;
}

/**
 * Route one work item. Precedence reflects what the professional must DO with it,
 * most-committing first: a not-yet-known instruction must enter the system; someone
 * waiting on a reply is next; a date must not be missed; then documents, money,
 * generic actions; otherwise it simply belongs on its matter; else it's noise.
 */
export function routeWork(item: WorkItem): RoutingDecision {
  const m = item.matterId ?? null;

  if (item.isInstruction && !m) return dec('needs_new_matter', null, 'New instruction — not yet a matter');
  if (item.isDecision) return dec('needs_decision', m, 'An approval is blocking someone');
  if (item.needsReply) return dec('needs_reply', m, 'Someone is awaiting a reply');
  if (item.hasDeadline) return dec('needs_diary', m, 'Carries a date to diarise');
  if (item.isDocument) return dec('needs_document_review', m, 'A document to review');
  if (item.isBillable) return dec('needs_billing', m, 'Money to raise or reconcile');
  if (item.isActionable) return dec('needs_task', m, 'An action to perform');
  if (m) return dec('needs_existing_matter', m, 'Belongs on an existing matter');
  return dec('needs_nothing', null, 'Informational — no action');
}

/** Route a batch and group by disposition — the shape a Work Queue renders. */
export function routeBatch(items: WorkItem[]): Record<WorkDisposition, Array<{ item: WorkItem; decision: RoutingDecision }>> {
  const groups = emptyGroups();
  for (const item of items) {
    const decision = routeWork(item);
    groups[decision.disposition].push({ item, decision });
  }
  return groups;
}

export const DISPOSITION_LABELS: Record<WorkDisposition, string> = {
  needs_new_matter: 'Needs filing',
  needs_decision: 'Needs decision',
  needs_reply: 'Needs reply',
  needs_diary: 'Needs scheduling',
  needs_document_review: 'Needs review',
  needs_billing: 'Needs money',
  needs_task: 'To do',
  needs_existing_matter: 'File on matter',
  needs_nothing: 'No action',
};

/** The ONE verb the Work Queue puts on a card for each disposition (Rule 3 — one action). */
export const DISPOSITION_ACTION: Record<WorkDisposition, string> = {
  needs_new_matter: 'Open matter',
  needs_decision: 'Decide',
  needs_reply: 'Reply',
  needs_diary: 'Schedule',
  needs_document_review: 'Review',
  needs_billing: 'Resolve',
  needs_task: 'Open',
  needs_existing_matter: 'Open matter',
  needs_nothing: '',
};

/** The dispositions that are genuine WORK (things requiring an action). Everything else —
 *  FYI-on-a-matter and pure noise — is deliberately absent from the Work Queue. */
export const ACTIONABLE_DISPOSITIONS: WorkDisposition[] = [
  'needs_new_matter', 'needs_decision', 'needs_reply', 'needs_diary',
  'needs_document_review', 'needs_billing', 'needs_task',
];

function dec(disposition: WorkDisposition, matterId: string | null, reason: string): RoutingDecision {
  return { disposition, matterId, reason };
}

function emptyGroups(): Record<WorkDisposition, Array<{ item: WorkItem; decision: RoutingDecision }>> {
  return {
    needs_new_matter: [], needs_decision: [], needs_reply: [], needs_diary: [], needs_document_review: [],
    needs_billing: [], needs_task: [], needs_existing_matter: [], needs_nothing: [],
  };
}
